import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Eye, Users, MousePointer, TrendingUp, Globe, Smartphone, Monitor, 
  Tablet, RefreshCw, Calendar, ArrowUp, ArrowDown, BarChart3
} from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import api from '../../services/api';

const VisitorAnalyticsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await api.get('/analytics/visitors', { params: { period } });
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch visitor analytics:', error);
      // Set demo data for display
      setData({
        summary: {
          total_pageviews: 0,
          unique_visitors: 0,
          unique_sessions: 0,
          total_conversions: 0,
          conversion_rate: 0
        },
        device_breakdown: { desktop: 0, mobile: 0, tablet: 0 },
        top_pages: [],
        top_events: [],
        conversions: { by_type: {}, value_by_type: {} },
        daily_breakdown: []
      });
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, label, value, subValue, color, trend }) => (
    <div className="card p-4 border-l-4" style={{ borderLeftColor: color }}>
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-5 h-5" style={{ color }} />
        {trend !== undefined && (
          <span className={`flex items-center text-xs ${trend >= 0 ? 'text-green-500' : 'text-red-500'}`}>
            {trend >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
      <p className="text-2xl font-bold text-[var(--text-primary)]">{value}</p>
      <p className="text-xs text-[var(--text-muted)]">{label}</p>
      {subValue && <p className="text-xs text-[var(--text-secondary)] mt-1">{subValue}</p>}
    </div>
  );

  if (loading) {
    return (
      <div className="p-12 text-center" data-testid="analytics-loading">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-[var(--text-muted)]">Loading analytics...</p>
      </div>
    );
  }

  const { summary, device_breakdown, top_pages, top_events, conversions, daily_breakdown } = data || {};
  const totalDeviceViews = (device_breakdown?.desktop || 0) + (device_breakdown?.mobile || 0) + (device_breakdown?.tablet || 0);

  return (
    <div className="space-y-6" data-testid="visitor-analytics-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-[var(--primary)]" />
            Website Analytics
          </h2>
          <p className="text-[var(--text-muted)] text-sm mt-1">
            Track visitor traffic and conversion metrics
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchAnalytics}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard 
          icon={Eye} 
          label="Page Views" 
          value={summary?.total_pageviews || 0}
          color="#D4AF37"
        />
        <StatCard 
          icon={Users} 
          label="Unique Visitors" 
          value={summary?.unique_visitors || 0}
          color="#8B5CF6"
        />
        <StatCard 
          icon={Globe} 
          label="Sessions" 
          value={summary?.unique_sessions || 0}
          color="#3B82F6"
        />
        <StatCard 
          icon={MousePointer} 
          label="Conversions" 
          value={summary?.total_conversions || 0}
          color="#10B981"
        />
        <StatCard 
          icon={TrendingUp} 
          label="Conversion Rate" 
          value={`${summary?.conversion_rate || 0}%`}
          color="#F59E0B"
        />
      </div>

      {/* Device Breakdown & Top Pages */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Device Breakdown */}
        <div className="card p-6">
          <h3 className="font-bold mb-4 flex items-center gap-2">
            <Monitor className="w-5 h-5 text-[var(--primary)]" />
            Device Breakdown
          </h3>
          <div className="space-y-4">
            {[
              { icon: Monitor, label: 'Desktop', value: device_breakdown?.desktop || 0, color: '#3B82F6' },
              { icon: Smartphone, label: 'Mobile', value: device_breakdown?.mobile || 0, color: '#10B981' },
              { icon: Tablet, label: 'Tablet', value: device_breakdown?.tablet || 0, color: '#F59E0B' }
            ].map(device => {
              const percentage = totalDeviceViews > 0 ? Math.round((device.value / totalDeviceViews) * 100) : 0;
              return (
                <div key={device.label} className="flex items-center gap-3">
                  <device.icon className="w-5 h-5" style={{ color: device.color }} />
                  <div className="flex-1">
                    <div className="flex justify-between text-sm mb-1">
                      <span>{device.label}</span>
                      <span className="font-medium">{device.value} ({percentage}%)</span>
                    </div>
                    <div className="h-2 bg-[var(--background-subtle)] rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%`, backgroundColor: device.color }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Pages */}
        <div className="card p-6">
          <h3 className="font-bold mb-4 flex items-center gap-2">
            <Eye className="w-5 h-5 text-[var(--primary)]" />
            Top Pages
          </h3>
          {top_pages?.length > 0 ? (
            <div className="space-y-3">
              {top_pages.map((page, idx) => (
                <div key={page.page} className="flex items-center justify-between py-2 border-b border-[var(--border)] last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-[var(--primary)]/10 text-[var(--primary)] text-xs flex items-center justify-center font-bold">
                      {idx + 1}
                    </span>
                    <span className="text-sm truncate max-w-[150px]">{page.page}</span>
                  </div>
                  <span className="text-sm font-medium">{page.views} views</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[var(--text-muted)] text-sm text-center py-8">
              No page view data yet. Analytics will appear as visitors browse your site.
            </p>
          )}
        </div>
      </div>

      {/* Daily Chart & Conversions */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Daily Breakdown */}
        <div className="card p-6">
          <h3 className="font-bold mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[var(--primary)]" />
            Daily Traffic
          </h3>
          {daily_breakdown?.length > 0 ? (
            <div className="space-y-2">
              {daily_breakdown.slice(-7).map(day => {
                const maxViews = Math.max(...daily_breakdown.map(d => d.pageviews)) || 1;
                const barWidth = (day.pageviews / maxViews) * 100;
                return (
                  <div key={day.date} className="flex items-center gap-3">
                    <span className="text-xs text-[var(--text-muted)] w-20">{day.date}</span>
                    <div className="flex-1 h-6 bg-[var(--background-subtle)] rounded overflow-hidden">
                      <div 
                        className="h-full bg-[var(--primary)] rounded flex items-center justify-end pr-2"
                        style={{ width: `${Math.max(barWidth, 5)}%` }}
                      >
                        <span className="text-xs text-white font-medium">{day.pageviews}</span>
                      </div>
                    </div>
                    <span className="text-xs text-[var(--text-muted)] w-16">{day.unique_visitors} visitors</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-[var(--text-muted)] text-sm text-center py-8">
              No daily data yet. Check back after visitors start browsing.
            </p>
          )}
        </div>

        {/* Conversion Types */}
        <div className="card p-6">
          <h3 className="font-bold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[var(--primary)]" />
            Conversion Breakdown
          </h3>
          {Object.keys(conversions?.by_type || {}).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(conversions.by_type).map(([type, count]) => (
                <div key={type} className="flex items-center justify-between py-2 border-b border-[var(--border)] last:border-0">
                  <div>
                    <span className="text-sm font-medium capitalize">{type.replace(/_/g, ' ')}</span>
                    {conversions.value_by_type?.[type] > 0 && (
                      <p className="text-xs text-[var(--text-muted)]">
                        ${conversions.value_by_type[type].toFixed(2)} value
                      </p>
                    )}
                  </div>
                  <span className="px-2 py-1 bg-[var(--success)]/10 text-[var(--success)] text-sm rounded-full font-medium">
                    {count}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[var(--text-muted)] text-sm text-center py-8">
              No conversions tracked yet. Track bookings, calls, and form submissions automatically.
            </p>
          )}
        </div>
      </div>

      {/* Top Events */}
      {top_events?.length > 0 && (
        <div className="card p-6">
          <h3 className="font-bold mb-4 flex items-center gap-2">
            <MousePointer className="w-5 h-5 text-[var(--primary)]" />
            Top Events
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {top_events.map((event, idx) => (
              <div key={event.event} className="p-4 bg-[var(--background-subtle)] rounded-lg">
                <p className="text-xs text-[var(--text-muted)] capitalize">{event.event.replace(/_/g, ' ')}</p>
                <p className="text-2xl font-bold text-[var(--primary)]">{event.count}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {summary?.total_pageviews === 0 && (
        <div className="card p-12 text-center">
          <BarChart3 className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
          <h3 className="text-lg font-bold mb-2">Start Tracking Visitors</h3>
          <p className="text-[var(--text-muted)] max-w-md mx-auto">
            Analytics tracking is now active on your website. As visitors browse your homepage and book services, 
            you'll see traffic data, device breakdowns, and conversion metrics here.
          </p>
        </div>
      )}
    </div>
  );
};

export default VisitorAnalyticsTab;
